import type { MobileOutputs } from 'shared/api'
import { writeCachedSession, removeCachedSession } from 'shared/lib/auth/storage'
import { create } from 'zustand'

/** The authenticated user (mobile login user is the superset: includes telegramId). */
export type User = MobileOutputs['auth']['TelegramLogin']['user']
export type Role = User['role']
export type AdminPermission = User['adminPermissions'][number]
export type AuthMode = 'telegram' | 'browser-admin'
export type AuthStatus = 'unauthenticated' | 'authenticated'

export interface CachedSession {
	version: 3
	mode: AuthMode
	telegramId?: number
	token: string
	expiresAt: number
	user: User
	/** True when the session was installed via the dev tools account switcher. */
	isDevOverride?: boolean
}

interface AuthState {
	user: User
	token: string | null
	expiresAt: number | null
	telegramId: number | null
	mode: AuthMode | null
	status: AuthStatus
	error: string | null

	setSession: (session: CachedSession) => void
	setUser: (user: User) => void
	setError: (error: string | null) => void
	clearSession: () => void
}

const emptyUser: User = {
	_id: '',
	name: '',
	role: 'user',
	adminPermissions: [],
	username: '',
}

export const useAuthStore = create<AuthState>()((set) => ({
	user: emptyUser,
	token: null,
	expiresAt: null,
	telegramId: null,
	mode: null,
	status: 'unauthenticated',
	error: null,

	setSession: (session) => {
		writeCachedSession(session)
		set({
			user: session.user,
			token: session.token,
			expiresAt: session.expiresAt,
			telegramId: session.telegramId ?? null,
			mode: session.mode,
			status: 'authenticated',
			error: null,
		})
	},

	setUser: (user) =>
		set((state) => {
			if (state.token && state.expiresAt && state.mode) {
				writeCachedSession({
					version: 3,
					mode: state.mode,
					telegramId: state.telegramId ?? undefined,
					token: state.token,
					expiresAt: state.expiresAt,
					user,
				})
			}
			return { user }
		}),

	setError: (error) => set({ error }),

	clearSession: () =>
		set((state) => {
			if (state.mode) removeCachedSession(state.mode)
			return {
				user: emptyUser,
				token: null,
				expiresAt: null,
				telegramId: null,
				mode: null,
				status: 'unauthenticated',
				error: null,
			}
		}),
}))
